import React, { useState } from 'react';
import {
  Search, Filter, Download, ChevronLeft, ChevronRight,
  Eye, Store, Star, DollarSign, ShoppingBag, Package,
  TrendingUp, TrendingDown, Clock, CheckCircle, XCircle,
  AlertCircle, MapPin, Phone, Mail, Calendar, Award,
  Crown, Sparkles, Zap, User
} from 'lucide-react';
import { Link } from 'react-router-dom';

const PartnerSellers = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPerformance, setFilterPerformance] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const itemsPerPage = 6;

  // Static Mock Data
  const sellers = [
    {
      id: 'SEL-001',
      name: 'Green Thumb Nursery',
      owner: 'Rahul Sharma',
      email: 'contact@greenthumb.com',
      phone: '+91 98765 43210',
      location: 'Bengaluru, India',
      status: 'Active',
      joinDate: 'Jan 15, 2024',
      lastActive: 'Today, 10:30 AM',
      products: 156,
      orders: 1245,
      revenue: 458900,
      rating: 4.9,
      avatar: 'G',
      verified: true,
      performance: 'high'
    },
    {
      id: 'SEL-002',
      name: 'Flora Bliss',
      owner: 'Priya Patel',
      email: 'hello@florabliss.com',
      phone: '+91 98765 43211',
      location: 'Mumbai, India',
      status: 'Active',
      joinDate: 'Mar 22, 2024',
      lastActive: 'Yesterday, 3:15 PM',
      products: 89,
      orders: 845,
      revenue: 325600,
      rating: 4.8,
      avatar: 'F',
      verified: true,
      performance: 'high'
    },
    {
      id: 'SEL-003',
      name: 'City Gardens',
      owner: 'Amit Singh',
      email: 'info@citygardens.com',
      phone: '+91 98765 43212',
      location: 'Delhi, India',
      status: 'Active',
      joinDate: 'Dec 05, 2023',
      lastActive: '2 hours ago',
      products: 67,
      orders: 678,
      revenue: 245800,
      rating: 4.7,
      avatar: 'C',
      verified: true,
      performance: 'medium'
    },
    {
      id: 'SEL-004',
      name: 'Herbals Co.',
      owner: 'Sneha Gupta',
      email: 'contact@herbals.com',
      phone: '+91 98765 43213',
      location: 'Pune, India',
      status: 'Active',
      joinDate: 'Oct 01, 2024',
      lastActive: '1 day ago',
      products: 45,
      orders: 234,
      revenue: 187500,
      rating: 4.6,
      avatar: 'H',
      verified: false,
      performance: 'medium'
    },
    {
      id: 'SEL-005',
      name: 'Succulent World',
      owner: 'Vikram Verma',
      email: 'hello@succulentworld.com',
      phone: '+91 98765 43214',
      location: 'Chennai, India',
      status: 'Active',
      joinDate: 'Jun 18, 2024',
      lastActive: 'Today, 8:15 AM',
      products: 34,
      orders: 167,
      revenue: 145200,
      rating: 4.8,
      avatar: 'S',
      verified: true,
      performance: 'medium'
    },
    {
      id: 'SEL-006',
      name: 'Fern Factory',
      owner: 'Neha Singh',
      email: 'info@fernfactory.com',
      phone: '+91 98765 43215',
      location: 'Hyderabad, India',
      status: 'Pending',
      joinDate: 'Oct 20, 2024',
      lastActive: '3 days ago',
      products: 12,
      orders: 0,
      revenue: 0,
      rating: 0,
      avatar: 'F',
      verified: false,
      performance: 'low'
    },
    {
      id: 'SEL-007',
      name: 'Bonsai Masters',
      owner: 'Rajesh Kumar',
      email: 'contact@bonsaimasters.com',
      phone: '+91 98765 43216',
      location: 'Jaipur, India',
      status: 'Active',
      joinDate: 'Aug 10, 2024',
      lastActive: 'Yesterday, 6:20 PM',
      products: 23,
      orders: 89,
      revenue: 98700,
      rating: 4.7,
      avatar: 'B',
      verified: true,
      performance: 'low'
    },
  ];

  const stats = {
    total: sellers.length,
    active: sellers.filter(s => s.status === 'Active').length,
    pending: sellers.filter(s => s.status === 'Pending').length,
    totalRevenue: sellers.reduce((sum, s) => sum + s.revenue, 0),
    totalProducts: sellers.reduce((sum, s) => sum + s.products, 0),
    totalOrders: sellers.reduce((sum, s) => sum + s.orders, 0),
    avgRating: (sellers.reduce((sum, s) => sum + s.rating, 0) / sellers.filter(s => s.rating > 0).length).toFixed(1)
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'Active': return { icon: CheckCircle, color: '#10b981', bg: '#d1fae5' };
      case 'Pending': return { icon: Clock, color: '#f59e0b', bg: '#fed7aa' };
      case 'Inactive': return { icon: XCircle, color: '#ef4444', bg: '#fee2e2' };
      default: return { icon: AlertCircle, color: '#64748b', bg: '#f1f5f9' };
    }
  };

  const getPerformanceConfig = (performance) => {
    switch (performance) {
      case 'high': return { color: '#10b981', bg: '#d1fae5', label: 'High Performer' };
      case 'medium': return { color: '#f59e0b', bg: '#fed7aa', label: 'Medium Performer' };
      case 'low': return { color: '#ef4444', bg: '#fee2e2', label: 'Low Performer' };
      default: return { color: '#64748b', bg: '#f1f5f9', label: 'Unknown' };
    }
  };

  const filteredSellers = sellers.filter(seller => {
    const matchesSearch = seller.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          seller.owner.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          seller.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || seller.status === filterStatus;
    const matchesPerformance = filterPerformance === 'all' || seller.performance === filterPerformance;
    return matchesSearch && matchesStatus && matchesPerformance;
  });

  const totalPages = Math.ceil(filteredSellers.length / itemsPerPage);
  const paginatedSellers = filteredSellers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const StatCard = ({ title, value, icon: Icon, color, bgColor }) => (
    <div className="stat-card">
      <div className="stat-icon" style={{ backgroundColor: bgColor }}>
        <Icon size={20} color={color} />
      </div>
      <div className="stat-info">
        <h3>{typeof value === 'number' ? value.toLocaleString() : value}</h3>
        <span>{title}</span>
      </div>
    </div>
  );

  const getRatingStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    
    return (
      <div className="rating-stars">
        {[...Array(fullStars)].map((_, i) => (
          <Star key={`full-${i}`} size={12} fill="#f59e0b" color="#f59e0b" />
        ))}
        {hasHalfStar && <Star size={12} fill="#f59e0b" color="#f59e0b" className="half-star" />}
        {[...Array(emptyStars)].map((_, i) => (
          <Star key={`empty-${i}`} size={12} fill="transparent" color="#cbd5e1" />
        ))}
      </div>
    );
  };

  return (
    <div className="partner-sellers-modern">
      {/* Header */}
      <div className="sellers-header">
        <div>
          <h1 className="page-title">Sellers Overview</h1>
          <p className="page-subtitle">Monitor and track all sellers on the platform</p>
        </div>
        <div className="header-actions">
          <button className="btn-export">
            <Download size={16} />
            Export Sellers
          </button>
        </div>
      </div>

      {/* Read-Only Banner */}
      <div className="readonly-banner">
        <Eye size={18} />
        <span>You have read-only access. You can view seller data but cannot make changes.</span>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <StatCard title="Total Sellers" value={stats.total} icon={Store} color="#10b981" bgColor="#d1fae5" />
        <StatCard title="Active Sellers" value={stats.active} icon={CheckCircle} color="#3b82f6" bgColor="#dbeafe" />
        <StatCard title="Total Revenue" value={`₹${stats.totalRevenue.toLocaleString()}`} icon={DollarSign} color="#8b5cf6" bgColor="#e9d5ff" />
        <StatCard title="Total Orders" value={stats.totalOrders} icon={ShoppingBag} color="#f59e0b" bgColor="#fed7aa" />
        <StatCard title="Total Products" value={stats.totalProducts} icon={Package} color="#06b6d4" bgColor="#cffafe" />
        <StatCard title="Avg Rating" value={stats.avgRating} icon={Star} color="#ef4444" bgColor="#fee2e2" />
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input 
            type="text" 
            placeholder="Search by seller name, owner or email..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select className="filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="Active">Active</option>
            <option value="Pending">Pending</option>
          </select>
          <select className="filter-select" value={filterPerformance} onChange={(e) => setFilterPerformance(e.target.value)}>
            <option value="all">All Performers</option>
            <option value="high">High Performers</option>
            <option value="medium">Medium Performers</option>
            <option value="low">Low Performers</option>
          </select>
        </div>
      </div>

      {/* Results Info */}
      <div className="results-info">
        <span>{filteredSellers.length} Sellers Found</span>
      </div>

      {/* Sellers Grid */}
      <div className="sellers-grid">
        {paginatedSellers.map((seller) => {
          const statusConfig = getStatusConfig(seller.status);
          const StatusIcon = statusConfig.icon;
          const performanceConfig = getPerformanceConfig(seller.performance);
          
          return (
            <div key={seller.id} className="seller-card">
              <div className="seller-card-header">
                <div className="seller-avatar">
                  <span>{seller.avatar}</span>
                  {seller.verified && <div className="verified-badge">✓</div>}
                </div>
                <div className="seller-info">
                  <h4 className="seller-name">{seller.name}</h4>
                  <p className="seller-owner">by {seller.owner}</p>
                  <div className="seller-badges">
                    <span className="status-badge" style={{ backgroundColor: statusConfig.bg, color: statusConfig.color }}>
                      <StatusIcon size={12} />
                      {seller.status}
                    </span>
                    <span className="performance-badge" style={{ backgroundColor: performanceConfig.bg, color: performanceConfig.color }}>
                      {performanceConfig.label}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="seller-card-body">
                <div className="seller-contact">
                  <div className="contact-item">
                    <Mail size={14} />
                    <span>{seller.email}</span>
                  </div>
                  <div className="contact-item">
                    <Phone size={14} />
                    <span>{seller.phone}</span>
                  </div>
                  <div className="contact-item">
                    <MapPin size={14} />
                    <span>{seller.location}</span>
                  </div>
                </div>
                
                <div className="seller-metrics">
                  <div className="metric">
                    <Package size={14} />
                    <div>
                      <span className="metric-value">{seller.products}</span>
                      <span className="metric-label">Products</span>
                    </div>
                  </div>
                  <div className="metric">
                    <ShoppingBag size={14} />
                    <div>
                      <span className="metric-value">{seller.orders.toLocaleString()}</span>
                      <span className="metric-label">Orders</span>
                    </div>
                  </div>
                  <div className="metric">
                    <DollarSign size={14} />
                    <div>
                      <span className="metric-value">₹{seller.revenue.toLocaleString()}</span>
                      <span className="metric-label">Revenue</span>
                    </div>
                  </div>
                  <div className="metric">
                    <Star size={14} />
                    <div>
                      <div className="metric-rating">
                        {getRatingStars(seller.rating)}
                        <span className="metric-value">{seller.rating}</span>
                      </div>
                      <span className="metric-label">Rating</span>
                    </div>
                  </div>
                </div>
                
                <div className="seller-footer">
                  <div className="join-date">
                    <Calendar size={12} />
                    <span>Joined {seller.joinDate}</span>
                  </div>
                  <button className="view-details-btn" onClick={() => {
                    setSelectedSeller(seller);
                    setShowDetailsModal(true);
                  }}>
                    View Details →
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-wrapper">
          <div className="pagination-info">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredSellers.length)} of {filteredSellers.length} sellers
          </div>
          <div className="pagination-buttons">
            <button 
              className={`page-btn ${currentPage === 1 ? 'disabled' : ''}`} 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={16} /> Previous
            </button>
            {[...Array(totalPages)].map((_, i) => (
              <button 
                key={i}
                className={`page-number ${currentPage === i + 1 ? 'active' : ''}`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button 
              className={`page-btn ${currentPage === totalPages ? 'disabled' : ''}`} 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {filteredSellers.length === 0 && (
        <div className="empty-state">
          <Store size={64} className="empty-icon" />
          <h3>No sellers found</h3>
          <p>Try adjusting your search or filter criteria</p>
        </div>
      )}

      {/* Seller Details Modal */}
      {showDetailsModal && selectedSeller && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-info">
                <div className="modal-avatar">
                  <span>{selectedSeller.avatar}</span>
                </div>
                <div>
                  <h3>{selectedSeller.name}</h3>
                  <p className="modal-id">{selectedSeller.id}</p>
                </div>
              </div>
              <button className="modal-close" onClick={() => setShowDetailsModal(false)}>×</button>
            </div>

            <div className="modal-body">
              <div className="detail-section">
                <h4>Seller Information</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <User size={14} />
                    <span>Owner: {selectedSeller.owner}</span>
                  </div>
                  <div className="info-item">
                    <Mail size={14} />
                    <span>{selectedSeller.email}</span>
                  </div>
                  <div className="info-item">
                    <Phone size={14} />
                    <span>{selectedSeller.phone}</span>
                  </div>
                  <div className="info-item">
                    <MapPin size={14} />
                    <span>{selectedSeller.location}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>Performance Metrics</h4>
                <div className="stats-grid-modal">
                  <div className="modal-stat">
                    <Package size={18} />
                    <div>
                      <div className="modal-stat-value">{selectedSeller.products}</div>
                      <div className="modal-stat-label">Products</div>
                    </div>
                  </div>
                  <div className="modal-stat">
                    <ShoppingBag size={18} />
                    <div>
                      <div className="modal-stat-value">{selectedSeller.orders.toLocaleString()}</div>
                      <div className="modal-stat-label">Orders</div>
                    </div>
                  </div>
                  <div className="modal-stat">
                    <DollarSign size={18} />
                    <div>
                      <div className="modal-stat-value">₹{selectedSeller.revenue.toLocaleString()}</div>
                      <div className="modal-stat-label">Revenue</div>
                    </div>
                  </div>
                  <div className="modal-stat">
                    <Star size={18} />
                    <div>
                      <div className="modal-stat-value">{selectedSeller.rating}</div>
                      <div className="modal-stat-label">Rating</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>Account Information</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <Calendar size={14} />
                    <span>Joined: {selectedSeller.joinDate}</span>
                  </div>
                  <div className="info-item">
                    <Clock size={14} />
                    <span>Last Active: {selectedSeller.lastActive}</span>
                  </div>
                  <div className="info-item">
                    <Award size={14} />
                    <span>Status: {selectedSeller.status}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDetailsModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
      <style>{`
        .partner-sellers-modern {
          padding: 24px;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          min-height: 100vh;
        }

        /* Header */
        .sellers-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 28px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .page-title {
          font-size: 28px;
          font-weight: 700;
          background: linear-gradient(135deg, #1a472a 0%, #2d6a4f 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 4px;
        }

        .page-subtitle {
          color: #64748b;
          font-size: 14px;
          margin: 0;
        }

        .header-actions {
          display: flex;
          gap: 12px;
        }

        .btn-export {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 20px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 40px;
          font-size: 13px;
          cursor: pointer;
        }

        .btn-export:hover {
          border-color: #10b981;
          color: #10b981;
        }

        /* Read-Only Banner */
        .readonly-banner {
          background: #fef3c7;
          border: 1px solid #fde68a;
          border-radius: 12px;
          padding: 12px 20px;
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 24px;
          color: #92400e;
          font-size: 13px;
        }

        /* Stats Cards */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 16px;
          margin-bottom: 28px;
        }

        .stat-card {
          background: white;
          border-radius: 16px;
          padding: 16px;
          display: flex;
          align-items: center;
          gap: 12px;
          border: 1px solid #e8ecef;
          transition: all 0.3s ease;
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.08);
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 14px;
        }

        .stat-info h3 {
          font-size: 20px;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
        }

        .stat-info span {
          font-size: 11px;
          color: #64748b;
        }

        /* Filters */
        .filters-bar {
          background: white;
          border-radius: 16px;
          padding: 12px 20px;
          margin-bottom: 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
          border: 1px solid #e8ecef;
        }

        .search-box {
          position: relative;
          flex: 1;
          max-width: 320px;
        }

        .search-box svg {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
        }

        .search-box input {
          width: 100%;
          padding: 8px 16px 8px 38px;
          border: 1px solid #e2e8f0;
          border-radius: 40px;
          font-size: 13px;
        }

        .filter-group {
          display: flex;
          gap: 12px;
        }

        .filter-select {
          padding: 6px 16px;
          border: 1px solid #e2e8f0;
          border-radius: 40px;
          font-size: 13px;
          background: white;
        }

        /* Results Info */
        .results-info {
          margin-bottom: 20px;
          font-size: 13px;
          color: #64748b;
        }

        /* Sellers Grid */
        .sellers-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
          gap: 24px;
          margin-bottom: 24px;
        }

        .seller-card {
          background: white;
          border-radius: 20px;
          overflow: hidden;
          transition: all 0.3s ease;
          border: 1px solid #e8ecef;
        }

        .seller-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(0,0,0,0.1);
        }

        .seller-card-header {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 20px;
          background: #f8fafc;
          border-bottom: 1px solid #e8ecef;
        }

        .seller-avatar {
          position: relative;
          width: 64px;
          height: 64px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 24px;
          font-weight: 600;
        }

        .verified-badge {
          position: absolute;
          bottom: 0;
          right: 0;
          width: 20px;
          height: 20px;
          background: #10b981;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          color: white;
          border: 2px solid white;
        }

        .seller-name {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 4px;
        }

        .seller-owner {
          font-size: 12px;
          color: #64748b;
          margin: 0 0 8px;
        }

        .seller-badges {
          display: flex;
          gap: 8px;
        }

        .status-badge, .performance-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 3px 8px;
          border-radius: 20px;
          font-size: 10px;
          font-weight: 500;
        }

        .seller-card-body {
          padding: 16px 20px;
        }

        .seller-contact {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 16px;
        }

        .contact-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          color: #64748b;
        }

        .seller-metrics {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
          padding: 12px 0;
          border-top: 1px solid #e8ecef;
          border-bottom: 1px solid #e8ecef;
          margin-bottom: 12px;
        }

        .metric {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .metric div {
          display: flex;
          flex-direction: column;
        }

        .metric-value {
          font-size: 14px;
          font-weight: 700;
          color: #1e293b;
        }

        .metric-label {
          font-size: 10px;
          color: #64748b;
        }

        .metric-rating {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .seller-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .join-date {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          color: #94a3b8;
        }

        .view-details-btn {
          background: none;
          border: none;
          color: #10b981;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
        }

        .view-details-btn:hover {
          text-decoration: underline;
        }

        /* Pagination */
        .pagination-wrapper {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 0;
          flex-wrap: wrap;
          gap: 16px;
          border-top: 1px solid #e8ecef;
        }

        .pagination-info {
          font-size: 13px;
          color: #64748b;
        }

        .pagination-buttons {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .page-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 14px;
          border: 1px solid #e2e8f0;
          background: white;
          border-radius: 8px;
          font-size: 13px;
          cursor: pointer;
        }

        .page-btn.disabled, .page-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .page-number {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid #e2e8f0;
          background: white;
          border-radius: 8px;
          cursor: pointer;
          font-size: 13px;
        }

        .page-number.active {
          background: #10b981;
          border-color: #10b981;
          color: white;
        }

        /* Empty State */
        .empty-state {
          text-align: center;
          padding: 60px 20px;
          background: white;
          border-radius: 20px;
        }

        .empty-icon {
          color: #94a3b8;
          margin-bottom: 16px;
        }

        .empty-state h3 {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 8px;
        }

        .empty-state p {
          font-size: 13px;
          color: #64748b;
          margin: 0;
        }

        /* Modal */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          backdrop-filter: blur(4px);
        }

        .modal-container {
          background: white;
          border-radius: 24px;
          width: 90%;
          max-width: 500px;
          max-height: 85vh;
          overflow-y: auto;
          animation: modalSlideIn 0.3s ease;
        }

        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #e8ecef;
        }

        .modal-header-info {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .modal-avatar {
          width: 56px;
          height: 56px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 24px;
          font-weight: 600;
        }

        .modal-header h3 {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }

        .modal-id {
          font-size: 11px;
          color: #94a3b8;
          margin-top: 4px;
        }

        .modal-close {
          width: 32px;
          height: 32px;
          background: #f1f5f9;
          border: none;
          border-radius: 8px;
          font-size: 20px;
          cursor: pointer;
        }

        .modal-body {
          padding: 24px;
        }

        .detail-section {
          margin-bottom: 24px;
        }

        .detail-section h4 {
          font-size: 14px;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 12px;
          padding-bottom: 8px;
          border-bottom: 1px solid #e8ecef;
        }

        .info-grid {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .info-item {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 13px;
          color: #475569;
        }

        .stats-grid-modal {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
        }

        .modal-stat {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px;
          background: #f8fafc;
          border-radius: 12px;
        }

        .modal-stat-value {
          font-size: 16px;
          font-weight: 700;
          color: #1e293b;
        }

        .modal-stat-label {
          font-size: 10px;
          color: #64748b;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 24px;
          border-top: 1px solid #e8ecef;
        }

        .btn-secondary {
          padding: 8px 20px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 40px;
          font-size: 13px;
          cursor: pointer;
        }

        /* Responsive */
        @media (max-width: 1200px) {
          .stats-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        @media (max-width: 768px) {
          .partner-sellers-modern {
            padding: 16px;
          }
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .filters-bar {
            flex-direction: column;
            align-items: stretch;
          }
          .search-box {
            max-width: none;
          }
          .sellers-grid {
            grid-template-columns: 1fr;
          }
          .seller-metrics {
            grid-template-columns: 1fr;
          }
          .stats-grid-modal {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default PartnerSellers;